import React from 'react';
import { Helmet } from 'react-helmet-async';
import { ArrowRight, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Card } from './ui/card';

const OurStory = () => {
  const handleWhatsAppClick = () => {
    const message = encodeURIComponent("Hi! I'd like to plan my holiday with Ukiyo Tours.");
    window.open(`https://wa.me/918591732501?text=${message}`, '_blank');
  };

  const travelImages = [
    {
      url: "https://images.unsplash.com/photo-1661747340818-df15f186554e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Njl8MHwxfHNlYXJjaHwxfHxrYXNobWlyJTIwdmFsbGV5fGVufDB8fHx8MTc3NjcxMzc3M3ww&ixlib=rb-4.1.0&q=85",
      location: "Kashmir Valley"
    },
    {
      url: "https://images.unsplash.com/photo-1766051224978-a57732014f9a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHw0fHxrZXJhbGElMjBiYWNrd2F0ZXJzJTIwaG91c2Vib2F0fGVufDB8fHx8MTc3NjcxMzgwNHww&ixlib=rb-4.1.0&q=85",
      location: "Kerala Backwaters"
    },
    {
      url: "https://images.unsplash.com/photo-1629184950099-3eb7993b5f48?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzl8MHwxfHNlYXJjaHwxfHxoaW1hbGF5YSUyMG1vdW50YWluc3xlbnwwfHx8fDE3NzY3MTM3Nzl8MA&ixlib=rb-4.1.0&q=85",
      location: "Himalayan Peaks"
    },
    {
      url: "https://images.unsplash.com/photo-1552465011-b4e21bf6e79a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDQ2Mzl8MHwxfHNlYXJjaHwzfHx0aGFpbGFuZCUyMGJlYWNofGVufDB8fHx8MTc3NjcxMzgyOHww&ixlib=rb-4.1.0&q=85",
      location: "Southeast Asia"
    },
    {
      url: "https://images.unsplash.com/photo-1593186344142-ef775a6e596f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MDV8MHwxfHNlYXJjaHwzfHxzd2lzcyUyMGFscHN8ZW58MHx8fHwxNzc2NzEzODMzfDA&ixlib=rb-4.1.0&q=85",
      location: "Swiss Alps"
    },
    {
      url: "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwzfHxwYXJpc3xlbnwwfHx8fDE3NzY3MTM4Mzd8MA&ixlib=rb-4.1.0&q=85",
      location: "Paris"
    }
  ];

  const philosophy = [
    "Travel is not about spending more, but spending right",
    "Some journeys are about exploration, others about indulgence",
    "The best trips balance experience, comfort, and value",
    "Every itinerary should feel personal, not packaged"
  ];

  const trustPoints = [
    "Real travel experience across destinations",
    "Balanced approach: smart spending + premium moments",
    "Personalized itinerary planning",
    "Direct support and guidance"
  ];

  return (
    <div className="bg-white">
      <Helmet>
        <title>Our Story | Ukiyo Tours</title>
        <meta name="description" content="Built on a love for travel — real journeys across India, Southeast Asia, Europe & beyond. Meet Jiya Vishwamitra and discover the Ukiyo Tours story." />
        
        {/* Open Graph - Page Specific */}
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Our Story | Ukiyo Tours" />
        <meta property="og:description" content="Built on a love for travel — real journeys across India, Southeast Asia, Europe & beyond." />
        <meta property="og:url" content="https://www.ukiyotours.in/our-story" />
        <meta property="og:image" content="https://www.ukiyotours.in/og-image.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        
        {/* Twitter - Page Specific */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Our Story | Ukiyo Tours" />
        <meta name="twitter:description" content="Built on a love for travel — real journeys across India, Southeast Asia, Europe & beyond." />
        <meta name="twitter:image" content="https://www.ukiyotours.in/og-image.jpg" />
        
        {/* Canonical - Page Specific */}
        <link rel="canonical" href="https://www.ukiyotours.in/our-story" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src="https://images.unsplash.com/photo-1698726654908-834d3a5330d8?q=80&w=1920"
            alt="Travel Journey"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-[#0B1F3A]/50"></div>
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-6 lg:px-8 py-32 text-center">
          <h1 className="heading-xl text-white mb-8 animate-fade-in-up">
            Built on a Love for Travel
          </h1>
          <p className="text-xl md:text-2xl text-white/95 mb-12 leading-relaxed max-w-3xl mx-auto animate-fade-in-up font-light" style={{ animationDelay: '0.2s' }}>
            A journey shaped by experience, curiosity, and a passion for meaningful travel
          </p>
          <Button 
            onClick={handleWhatsAppClick}
            size="lg"
            className="bg-[#B8964F] hover:bg-[#a67f3a] text-white text-lg px-12 py-7 rounded-xl shadow-2xl hover:shadow-3xl transition-all duration-300 hover:scale-105 group animate-fade-in-up"
            style={{ animationDelay: '0.4s' }}
          >
            Plan Your Holiday on WhatsApp
            <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform duration-300" size={24} />
          </Button>
        </div>
      </section>

      {/* Founder Story Section */}
      <section className="section-spacing bg-white">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-16 items-center mb-20">
            <div className="animate-fade-in-up">
              <img 
                src="https://customer-assets.emergentagent.com/job_maldives-escapes-1/artifacts/gb5oa0ab_image.png"
                alt="Jiya Vishwamitra"
                className="w-full rounded-2xl shadow-2xl"
                loading="lazy"
              />
            </div>
            <div className="animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              <h2 className="heading-lg text-[#0B1F3A] mb-6">
                Meet Jiya Vishwamitra
              </h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Founder of Ukiyo Tours
              </p>
            </div>
          </div>

          <div className="prose prose-lg max-w-none">
            <div className="space-y-6 text-gray-700 leading-relaxed animate-fade-in-up" style={{ animationDelay: '0.3s' }}>
              <p className="text-xl leading-relaxed">
                Ukiyo Tours was built on a simple idea — travel should be personal, flexible, and thoughtfully designed around how people actually like to explore the world.
              </p>

              <p className="text-lg leading-relaxed">
                At the heart of this philosophy is Jiya Vishwamitra, whose passion for travel comes from years of experiencing destinations in different ways — sometimes backpacking to discover places up close, and at other times unwinding in carefully chosen luxury stays.
              </p>

              <p className="text-lg leading-relaxed">
                This balanced approach comes from a deeper belief: not every part of a journey needs to be extravagant, but the right moments should feel truly special. By blending practicality with indulgence, she has been able to travel frequently across the year while making each trip meaningful and memorable.
              </p>

              <p className="text-lg leading-relaxed">
                Her journeys span across India — from the mountains of the North and Northeast to the coasts of the South — as well as international destinations including Bhutan, Southeast Asia, the Middle East, and parts of Europe including the UK.
              </p>

              <p className="text-lg leading-relaxed">
                Along the way, she has developed a keen eye for value, timing, and experience — learning what to prioritize, where to save, and where it truly matters to spend.
              </p>

              <p className="text-lg leading-relaxed">
                Ukiyo Tours brings this perspective into every itinerary — helping travelers get the most out of their trips, discover hidden value, and enjoy seamless, well-balanced travel experiences.
              </p>

              <p className="text-lg leading-relaxed font-medium text-[#0B1F3A]">
                At its core, it's about more than just destinations — it's about creating meaningful journeys and genuine connections along the way.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Visual Story Strip */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="heading-lg text-[#0B1F3A] mb-4 animate-fade-in-up">
              A Journey Across Destinations
            </h2>
            <p className="text-lg text-gray-600 animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
              From mountains to beaches, cities to valleys
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
            {travelImages.map((image, index) => (
              <Card 
                key={index}
                className="group overflow-hidden border-none shadow-md hover:shadow-2xl transition-all duration-500 cursor-pointer animate-fade-in-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="relative h-72 overflow-hidden">
                  <img 
                    src={image.url}
                    alt={image.location}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                    loading="lazy"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0B1F3A]/70 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                  <div className="absolute bottom-4 left-4 right-4 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <p className="font-medium text-lg">{image.location}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="section-spacing bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="heading-lg text-[#0B1F3A] mb-16 animate-fade-in-up">
            How We Think About Travel
          </h2>

          <div className="space-y-8">
            {philosophy.map((point, index) => (
              <p 
                key={index}
                className="text-xl md:text-2xl text-gray-700 leading-relaxed font-light animate-fade-in-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                {point}
              </p>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Builder Section */}
      <section className="py-20 bg-[#0B1F3A]">
        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8">
            {trustPoints.map((point, index) => (
              <div 
                key={index}
                className="flex items-start space-x-4 animate-fade-in-up"
                style={{ animationDelay: `${index * 0.1}s` }}
              >
                <div className="flex-shrink-0 w-8 h-8 bg-[#B8964F] rounded-full flex items-center justify-center mt-1">
                  <Check className="text-white" size={18} strokeWidth={3} />
                </div>
                <p className="text-lg text-white/95 leading-relaxed">{point}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-spacing bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="heading-lg text-[#0B1F3A] mb-6 animate-fade-in-up">
            Let's Plan Your Journey
          </h2>
          <p className="text-xl text-gray-600 mb-12 leading-relaxed animate-fade-in-up" style={{ animationDelay: '0.1s' }}>
            Tell us your travel plans, and we'll curate the best possible experience for you
          </p>
          <Button 
            onClick={handleWhatsAppClick}
            size="lg"
            className="bg-[#B8964F] hover:bg-[#a67f3a] text-white text-lg px-12 py-7 rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 hover:scale-105 group animate-fade-in-up"
            style={{ animationDelay: '0.2s' }}
          >
            Start on WhatsApp
            <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform duration-300" size={24} />
          </Button>
        </div>
      </section>
    </div>
  );
};

export default OurStory;
